import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { NavArrowLeft, Plus, Settings, BrainElectricity, Medal1st, ArrowUpRight, ArrowRight, ArrowDownRight } from 'iconoir-react';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer } from 'recharts';
import { C, CARD_STYLE, St, FONT_BODY, FONT_MONO, SPRING } from '../theme';
import { _globalSettings } from '../constants';
import { weeklyVol, calcVolume, classifyAllExercises4State } from '../science';
import { buildColorMap, rankSchede } from '../utils';
import { openExHistory } from '../events';

import { UserArea } from '../components/UserArea';
import { ExercisePreview } from '../components/Exercise';
import { SessionStats } from '../components/Session';
import { FolderIcon } from '../components/Folder';
import { supabase } from '../supabase';
import { buildGeminiContext } from '../gemini';
import { parseExerciseBlocks, saveCoachAdvices, STATO_COLORS } from './CoachChatPage';


const _MONTHS=['gen','feb','mar','apr','mag','giu','lug','ago','set','ott','nov','dic'];
const fmtDateIt=ts=>{if(!ts)return'';const d=new Date(ts);return`${d.getDate()} ${_MONTHS[d.getMonth()]}`;};

function SegmentedBar({segments,fallbackColor,exCount}){
  const [tooltip,setTooltip]=useState(null);
  const barRef=useRef(null);
  const hideTimer=useRef(null);

  const showAt=(clientX)=>{
    if(!barRef.current||!segments.length)return;
    const rect=barRef.current.getBoundingClientRect();
    const ratio=(clientX-rect.left)/rect.width;
    const total=segments.reduce((s,x)=>s+x.count,0);
    let cum=0;
    for(const seg of segments){
      cum+=seg.count/total;
      if(ratio<=cum){
        setTooltip({label:`${seg.count} ${seg.label}`,color:seg.color,ratio:Math.min(Math.max(ratio,0),1)});
        return;
      }
    }
  };

  const hide=()=>{
    hideTimer.current=setTimeout(()=>setTooltip(null),120);
  };
  const cancelHide=()=>{
    if(hideTimer.current)clearTimeout(hideTimer.current);
  };

  return(
    <div style={{marginTop:10,position:'relative',userSelect:'none'}}>
      {/* Tooltip */}
      {tooltip&&(
        <div style={{
          position:'absolute',
          bottom:'calc(100% + 6px)',
          left:`calc(${tooltip.ratio*100}% - 40px)`,
          background:'rgba(28,28,30,0.88)',
          backdropFilter:'blur(8px)',
          WebkitBackdropFilter:'blur(8px)',
          color:'#fff',
          fontSize:12,
          fontWeight:600,
          fontFamily:FONT_BODY,
          padding:'5px 10px',
          borderRadius:8,
          whiteSpace:'nowrap',
          pointerEvents:'none',
          zIndex:10,
          boxShadow:'0 2px 8px rgba(0,0,0,0.18)',
          display:'flex',
          alignItems:'center',
          gap:5,
        }}>
          <div style={{width:6,height:6,borderRadius:'50%',background:tooltip.color,flexShrink:0}}/>
          {tooltip.label}
        </div>
      )}
      {/* Bar */}
      <div
        ref={barRef}
        style={{display:'flex',borderRadius:6,overflow:'hidden',height:6,gap:2,cursor:'default'}}
        onMouseMove={e=>{cancelHide();showAt(e.clientX);}}
        onMouseLeave={hide}
        onTouchStart={e=>{if(e.touches[0])showAt(e.touches[0].clientX);}}
        onTouchMove={e=>{if(e.touches[0]){cancelHide();showAt(e.touches[0].clientX);}}}
        onTouchEnd={hide}
      >
        {segments.length>0?segments.map(s=>(
          <div key={s.key} style={{flex:s.count,background:s.color,borderRadius:6}}/>
        )):(
          <div style={{flex:1,background:fallbackColor||'rgba(120,120,128,0.12)',borderRadius:6}}/>
        )}
      </div>
      {exCount>0&&(
        <div style={{marginTop:6,fontSize:11,color:'rgba(60,60,67,0.45)',fontFamily:FONT_BODY}}>
          {exCount} {exCount===1?'esercizio':'esercizi'}
        </div>
      )}
    </div>
  );
}

function SchedaRow({scheda,col,advices,ts,isStale,onTap,isLast}){
  const hasAdvices=Array.isArray(advices)&&advices.length>0;
  const counts=hasAdvices?advices.reduce((acc,adv)=>{const k=(adv.stato||'').toLowerCase().trim();acc[k]=(acc[k]||0)+1;return acc;},{}):{};
  const prC=counts['pr']||0,progC=counts['progressione']||0,stalloC=counts['stallo']||0,regC=counts['regressione']||0;
  const parts=[];
  if(prC>0)parts.push(`${prC} PR`);
  if(progC>0)parts.push(`${progC} Prog`);
  if(stalloC>0)parts.push(`${stalloC} Stallo`);
  if(regC>0)parts.push(`${regC} Reg`);
  const subtitle=parts.length>0?parts.join(' · '):ts?'Analisi disponibile':'Nessuna analisi';
  const subtitleColor=regC>0?'#CC2800':stalloC>0?'#CC7000':prC>0?'#0060CC':progC>0?'#1A9E45':'#8E8E93';
  const daysAgo=scheda.daysAgo;
  const daysLabel=daysAgo>=999?'Mai':daysAgo===0?'Oggi':daysAgo===1?'Ieri':`${daysAgo} gg fa`;
  const daysColor=daysAgo>=999?'#FF3B30':daysAgo<=5?'#34C759':'#FF9500';
  return(
    <div onClick={onTap} style={{display:'flex',alignItems:'center',gap:12,padding:'0 16px',minHeight:64,cursor:'pointer',WebkitTapHighlightColor:'transparent',background:'transparent',borderBottom:isLast?'none':'0.5px solid rgba(60,60,67,0.08)',position:'relative'}}>
      <div style={{flexShrink:0}}><FolderIcon accentColor={col} size={32}/></div>
      <div style={{flex:1,minWidth:0,paddingTop:1}}>
        <div style={{fontSize:15,fontWeight:600,color:'#000',fontFamily:FONT_BODY,overflow:'hidden',whiteSpace:'nowrap',textOverflow:'ellipsis',lineHeight:1.25,marginBottom:2}}>{scheda.name}</div>
        <div style={{display:'flex',alignItems:'center',gap:5}}>
          {isStale&&hasAdvices&&<div style={{width:6,height:6,borderRadius:'50%',background:'#FF9500',flexShrink:0}}/>}
          <span style={{fontSize:12,fontWeight:500,color:subtitleColor,fontFamily:FONT_BODY,overflow:'hidden',whiteSpace:'nowrap',textOverflow:'ellipsis'}}>{subtitle}</span>
        </div>
      </div>
      <div style={{display:'flex',alignItems:'center',gap:4,flexShrink:0}}>
        <span style={{fontSize:12,fontWeight:600,color:daysColor,fontFamily:FONT_BODY}}>{daysLabel}</span>
        <svg width="7" height="12" viewBox="0 0 7 12" fill="none"><path d="M1 1l5 5-5 5" stroke="rgba(60,60,67,0.30)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
      </div>
    </div>
  );
}

export function DashboardPage({db,setDB,user,onStartSession,onGoToCoach,onReplayOnboarding,onGoToEsercizi,onGoToSchede,onOpenLauncher,onGoToCronologia,onGoToExercise,widgets}) {
  const wv=useMemo(()=>weeklyVol(db.sessions),[db.sessions]);
  const perfMap=useMemo(()=>classifyAllExercises4State(db.sessions),[db.sessions]);
  const [showUserArea,setShowUserArea]=useState(false);
  const [selectedWeekOffset,setSelectedWeekOffset]=useState(0);
  const [showVolumeDetail,setShowVolumeDetail]=useState(false);
  const [coachAdvices,setCoachAdvices]=useState(null);
  const [coachAdvicesTs,setCoachAdvicesTs]=useState(null);
  const [coachLoading,setCoachLoading]=useState(false);
  const [coachError,setCoachError]=useState(null);
  const coachSchedaRef=useRef(null);
  const [selectedDayISO,setSelectedDayISO]=useState(null);
  const [customKey,setCustomKey]=useState(0);
  const [widgetExpanded,setWidgetExpanded]=useState(false);
  useEffect(()=>{
    const h=()=>setCustomKey(k=>k+1);
    window.addEventListener('schedaCustomChanged',h);
    return()=>window.removeEventListener('schedaCustomChanged',h);
  },[]);

  const weekBounds=useMemo(()=>{
    const today=new Date();
    const mon=new Date(today);
    mon.setDate(today.getDate()-((today.getDay()+6)%7)+(selectedWeekOffset*7));
    const sun=new Date(mon);
    sun.setDate(mon.getDate()+6);
    const pad=(n)=>String(n).padStart(2,'0');
    const fmt=d=>[d.getFullYear(),pad(d.getMonth()+1),pad(d.getDate())].join('-');
    return{monISO:fmt(mon),sunISO:fmt(sun),mon,sun};
  },[selectedWeekOffset]);
  const weekSessions=useMemo(()=>
    [...db.sessions].filter(s=>s.date>=weekBounds.monISO&&s.date<=weekBounds.sunISO).sort((a,b)=>b.date.localeCompare(a.date)),
  [db.sessions,weekBounds]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const colorMap=useMemo(()=>buildColorMap(db.schede||[],db.sessions),[db.schede,db.sessions,customKey]);
  const ranked=useMemo(()=>rankSchede((db.schede||[]).filter(s=>s.active!==false),db.sessions),[db.schede,db.sessions]);



  // Carica analisi Coach AI per la prossima scheda dal localStorage
  useEffect(()=>{
    if(!ranked.length)return;
    const schedaName=ranked[0].name;
    if(coachSchedaRef.current===schedaName)return;
    coachSchedaRef.current=schedaName;
    setCoachAdvices(null);setCoachAdvicesTs(null);setCoachError(null);
    try{
      const raw=localStorage.getItem(`gp_coach_advices_${schedaName}`);
      if(raw){
        const{ts,advices}=JSON.parse(raw);
        if(Array.isArray(advices)&&advices.length){setCoachAdvices(advices);setCoachAdvicesTs(ts||null);return;}
      }
    }catch{}
  },[ranked]);


  const greetWord = _globalSettings?.profilo?.sesso === 'F' ? 'Bentornata' : 'Bentornato';
  const userName = (() => {
    try {
      const local = JSON.parse(localStorage.getItem(`gp_profile_${user?.id}`));
      if (local?.username) return local.username;
      if (local?.displayName) return local.displayName.split(' ')[0];
    } catch {}
    return (
      user?.user_metadata?.full_name ||
      user?.user_metadata?.name ||
      user?.user_metadata?.display_name ||
      user?.user_metadata?.username ||
      ''
    ).split(' ')[0];
  })();
  const weekOfMonth = useMemo(() => Math.ceil(weekBounds.mon.getDate() / 7), [weekBounds.mon]);
  const weekHeaderLabel = useMemo(() => {
    const d = weekBounds.mon;
    const day = d.toLocaleDateString('it-IT', { weekday: 'short' }).replace(/^\w/, c => c.toUpperCase());
    const date = d.toLocaleDateString('it-IT', { day: 'numeric', month: 'short' }).replace(/^\w/, c => c.toUpperCase());
    return `${day} ${date} · Settimana ${weekOfMonth}`;
  }, [weekBounds.mon, weekOfMonth]);
  const swipeTouchStart = useRef(null);
  const onSwipeTouchStart = useCallback(e => { swipeTouchStart.current = e.touches[0].clientX; }, []);
  const onSwipeTouchEnd = useCallback(e => {
    if (swipeTouchStart.current === null) return;
    const dx = e.changedTouches[0].clientX - swipeTouchStart.current;
    swipeTouchStart.current = null;
    if (Math.abs(dx) < 40) return;
    if (dx < 0) setSelectedWeekOffset(o => o - 1);
    else setSelectedWeekOffset(o => Math.min(0, o + 1));
  }, []);

  return (
    <div style={{...St.wrap,position:'relative'}}>
      <style>{`@keyframes fadeInDash{from{opacity:0}to{opacity:1}}@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}@keyframes gpSlideUp{from{opacity:0;transform:translateY(18px)}to{opacity:1;transform:translateY(0)}}@keyframes ptDraw{from{stroke-dashoffset:var(--pt-len)}to{stroke-dashoffset:0}}@keyframes ptFill{from{opacity:0}to{opacity:1}}@keyframes ptDot{from{transform:scale(0);opacity:0}to{transform:scale(1);opacity:1}}`}</style>
      <div
        style={{paddingTop:"max(56px, calc(env(safe-area-inset-top, 0px) + 14px))",paddingRight:"20px",paddingBottom:"16px",paddingLeft:"20px",position:'relative',zIndex:1,animation:`fadeInDash 0.6s ${SPRING} both`}}
        onTouchStart={onSwipeTouchStart}
        onTouchEnd={onSwipeTouchEnd}
      >
        <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:0}}>
          <div style={{animation:`gpSlideUp 0.5s 0.05s ${SPRING} both`}}>
            {/* Date — above greeting */}
            <div style={{marginBottom:4}}>
              <span style={{fontSize:13,fontWeight:500,color:C.subtle,fontFamily:FONT_BODY,letterSpacing:0}}>{weekHeaderLabel}</span>
            </div>
            <div style={{margin:0,fontFamily:FONT_BODY,fontWeight:800,fontSize:32,color:C.text,letterSpacing:-1,lineHeight:1.05}}>
              {greetWord}{userName ? <span style={{fontWeight:400,letterSpacing:-0.5}}>, {userName}.</span> : '.'}
            </div>
          </div>
          <button onClick={()=>setShowUserArea(true)} className="gp-btn-press"
            style={{width:38,height:38,borderRadius:19,background:"rgba(120,120,128,0.12)",border:"1px solid rgba(120,120,128,0.12)",cursor:"pointer",padding:0,flexShrink:0,marginTop:4,display:"flex",alignItems:"center",justifyContent:"center",transition:`transform 0.2s ${SPRING}`}}>
            <Settings width={20} height={20} color={C.text} strokeWidth={1.7}/>
          </button>
        </div>
      </div>
      <div style={{padding:"0 20px 0"}}>

      {/* Empty state — nessuna scheda e nessuna sessione */}
      {(db.schede||[]).length===0&&db.sessions.length===0&&(
        <div style={{...CARD_STYLE,padding:"28px 24px",marginBottom:16,textAlign:"center",animation:'fadeInDash 0.6s ease both'}}>
          <div style={{width:52,height:52,borderRadius:26,background:"rgba(0,122,255,0.10)",display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 16px"}}>
            <Plus width={26} height={26} color={C.blue} strokeWidth={2}/>
          </div>
          <div style={{fontSize:17,fontWeight:700,color:C.text,marginBottom:8,fontFamily:FONT_BODY,letterSpacing:-0.3}}>
            Tutto pronto
          </div>
          <div style={{fontSize:14,color:C.subtle,lineHeight:1.6,fontFamily:FONT_BODY,marginBottom:20}}>
            Crea la tua prima scheda con gli esercizi che vuoi tracciare. Poi registra la prima sessione e il coach inizierà ad analizzare i tuoi progressi.
          </div>
          <button onClick={onGoToEsercizi}
            style={{display:"inline-flex",alignItems:"center",gap:8,background:C.blue,color:"#fff",
              border:"none",borderRadius:8,padding:"12px 24px",fontSize:15,fontWeight:600,
              cursor:"pointer",fontFamily:FONT_BODY}}>
            <Plus width={16} height={16} color="#fff" strokeWidth={2.5}/>
            Crea la prima scheda
          </button>
        </div>
      )}

      {/* Coach narrative — include prossimo allenamento nell'overlay espandibile */}
      {db.sessions.length>0&&(()=>{
        const next=ranked.length>0?ranked[0]:null;
        const col=next?(colorMap[next.name]||C.blue):C.blue;
        const schedaObj=next?(db.schede||[]).find(s=>s.name===next.name):null;
        const exNames=(schedaObj?.exercises||[]).map(e=>e.name).filter(Boolean);
        const da=next?.daysAgo??999;
        const daysLabel=da>=999?'Mai allenata':da===0?'Oggi':da===1?'Ieri':`${da} gg fa`;
        const daysColor=da>=999||da>10?C.red:da>=2&&da<=5?C.green:da===1?C.amber:C.subtle;

        const handleRichiediAnalisi=async()=>{
          setCoachLoading(true);setCoachError(null);
          try{
            const{prompt,system}=buildGeminiContext(db.sessions,'session',{schedaName:next.name});
            const{data,error}=await supabase.functions.invoke('coach-analysis',{body:{prompt,system}});
            if(error)throw error;
            const text=data?.text||'';
            const lines=text.split('\n');
            const blocks=parseExerciseBlocks(lines);
            if(blocks.length){const now=Date.now();saveCoachAdvices(next.name,blocks,text);setCoachAdvices(blocks);setCoachAdvicesTs(now);}
            else{setCoachError('Nessun dato disponibile per questa scheda.');}
          }catch(e){setCoachError(e?.message||'Errore nel recupero analisi.');}
          finally{setCoachLoading(false);}
        };

        const statoKey=(s)=>(s||'').toLowerCase().trim();
        const statoStyleFn=(s)=>STATO_COLORS[statoKey(s)]||{bg:C.fill,color:C.subtle,border:C.fill};
        const statoIcon=(s,color)=>{
          const k=statoKey(s);
          const p={width:15,height:15,color,strokeWidth:1.8,style:{flexShrink:0}};
          if(k==='pr')return <Medal1st {...p}/>;
          if(k==='progressione')return <ArrowUpRight {...p}/>;
          if(k==='stallo')return <ArrowRight {...p}/>;
          if(k==='regressione')return <ArrowDownRight {...p}/>;
          return <div style={{width:7,height:7,borderRadius:'50%',background:color,flexShrink:0}}/>;
        };

        const toTitleCase=str=>str.split(' ').map(w=>w.length>0?w[0].toUpperCase()+w.slice(1).toLowerCase():w).join(' ');

        // Conta stati per la progress bar
        const stateCounts=coachAdvices?.length>0?coachAdvices.reduce((acc,a)=>{
          const k=(a.stato||'').toLowerCase().trim();
          acc[k]=(acc[k]||0)+1;return acc;
        },{}):{};
        const totalStates=coachAdvices?.length||0;
        const stateSegments=[
          {key:'pr',         color:'#007AFF', label:'PR'},
          {key:'progressione',color:'#34C759', label:'Progressione'},
          {key:'stallo',     color:'#FF9500', label:'Stallo'},
          {key:'regressione',color:'#FF3B30', label:'Regressione'},
        ].filter(s=>stateCounts[s.key]>0).map(s=>({...s,count:stateCounts[s.key]}));

        const nextWidget=next?(
          <div style={{...CARD_STYLE,marginBottom:0,overflow:'hidden',padding:0,background:'#fff',borderRadius:24}}>

            {/* ── HEADER COMPATTO (sempre visibile, cliccabile) ── */}
            <div
              onClick={()=>setWidgetExpanded(x=>!x)}
              style={{display:'flex',alignItems:'center',gap:14,padding:'16px 20px',cursor:'pointer',WebkitTapHighlightColor:'transparent'}}
            >
              <div style={{flexShrink:0}}>
                <FolderIcon accentColor={col} size={48}/>
              </div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:8,marginBottom:2}}>
                  <div style={{fontSize:16,fontWeight:700,letterSpacing:-0.4,color:C.text,fontFamily:FONT_BODY,lineHeight:1.2,flex:1,minWidth:0,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                    {next.name}
                  </div>
                  <div style={{display:'flex',alignItems:'center',gap:8,flexShrink:0}}>
                    <div style={{padding:'3px 9px',borderRadius:20,background:`${daysColor}14`}}>
                      <span style={{fontSize:12,fontWeight:600,color:daysColor,fontFamily:FONT_BODY}}>{daysLabel}</span>
                    </div>
                    {/* chevron espandi */}
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{flexShrink:0,transition:`transform 0.25s ${SPRING}`,transform:widgetExpanded?'rotate(180deg)':'rotate(0deg)'}}>
                      <path d="M2 4l4 4 4-4" stroke="rgba(60,60,67,0.35)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                </div>
                {exNames.length>0&&(
                  <SegmentedBar
                    segments={totalStates>0&&!coachLoading?stateSegments:[]}
                    fallbackColor={`${col}20`}
                    exCount={exNames.length}
                  />
                )}
                {coachLoading&&(
                  <div style={{display:'flex',alignItems:'center',gap:7,marginTop:8}}>
                    <div style={{width:12,height:12,borderRadius:'50%',border:`2px solid ${col}`,borderTopColor:'transparent',animation:'spin 0.8s linear infinite',flexShrink:0}}/>
                    <span style={{fontSize:11,color:C.subtle,fontFamily:FONT_BODY}}>Analisi in corso...</span>
                  </div>
                )}
              </div>
            </div>

            {/* ── SEZIONE ESPANDIBILE ── */}
            <div style={{
              maxHeight: widgetExpanded ? '1200px' : '0px',
              overflow: 'hidden',
              transition: `max-height 0.38s ${SPRING}`,
            }}>
              <div style={{height:'0.5px',background:'rgba(60,60,67,0.09)'}}/>

              {/* Lista esercizi */}
              {!coachLoading&&(coachAdvices?.length>0||coachError)&&(
                <div style={{padding:'0 24px'}}>
                  {coachAdvices?.length>0&&(
                    <div style={{display:'flex',flexDirection:'column'}}>
                      {coachAdvices.map((adv,i)=>{
                        const st=statoStyleFn(adv.stato);
                        const isLast=i===coachAdvices.length-1;
                        return(
                          <div key={i} onClick={()=>onGoToExercise&&onGoToExercise(adv.name)}
                            style={{display:'flex',alignItems:'center',gap:10,paddingTop:12,paddingBottom:12,
                              borderBottom:isLast?'none':'0.5px solid rgba(60,60,67,0.07)',
                              cursor:'pointer',WebkitTapHighlightColor:'transparent'}}>
                            <div style={{flexShrink:0,width:20,display:'flex',alignItems:'center',justifyContent:'center'}}>
                              {statoIcon(adv.stato,st.color)}
                            </div>
                            <div style={{flex:1,minWidth:0}}>
                              <div style={{fontSize:13,fontWeight:600,color:C.text,fontFamily:FONT_BODY,lineHeight:1.25,marginBottom:adv.obiettivo?2:0}}>
                                {toTitleCase(adv.name)}
                              </div>
                              {adv.obiettivo&&(
                                <div style={{fontSize:12,color:C.subtle,fontFamily:FONT_BODY,lineHeight:1.35,
                                  display:'-webkit-box',WebkitLineClamp:1,WebkitBoxOrient:'vertical',overflow:'hidden'}}>
                                  {adv.obiettivo}
                                </div>
                              )}
                            </div>
                            <svg width="7" height="12" viewBox="0 0 7 12" fill="none" style={{flexShrink:0}}>
                              <path d="M1 1l5 5-5 5" stroke="rgba(60,60,67,0.22)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {coachError&&(
                    <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:8,padding:'14px 0'}}>
                      <span style={{fontSize:12,color:C.red,fontFamily:FONT_BODY}}>{coachError}</span>
                      <button onClick={handleRichiediAnalisi} style={{border:'none',background:'transparent',padding:0,cursor:'pointer',fontSize:12,fontWeight:600,color:C.blue,fontFamily:FONT_BODY}}>Riprova</button>
                    </div>
                  )}
                </div>
              )}

              {/* CTA genera analisi */}
              {!coachLoading&&!coachAdvices?.length&&!coachError&&(
                <button onClick={handleRichiediAnalisi}
                  style={{display:'flex',alignItems:'center',gap:12,width:'100%',padding:'14px 24px',border:'none',background:'transparent',cursor:'pointer',WebkitTapHighlightColor:'transparent',textAlign:'left'}}>
                  <div style={{width:36,height:36,borderRadius:11,background:`${col}12`,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                    <BrainElectricity width={18} height={18} color={col} strokeWidth={2}/>
                  </div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:14,fontWeight:600,color:C.text,fontFamily:FONT_BODY,marginBottom:1}}>Genera analisi AI</div>
                    <div style={{fontSize:12,color:C.subtle,fontFamily:FONT_BODY}}>Progressione e obiettivi per ogni esercizio</div>
                  </div>
                  <svg width="7" height="12" viewBox="0 0 7 12" fill="none" style={{flexShrink:0}}>
                    <path d="M1 1l5 5-5 5" stroke="rgba(60,60,67,0.25)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              )}

              {/* Footer + CTA Apri analisi */}
              <div style={{padding:'14px 24px 24px',display:'flex',flexDirection:'column',gap:10}}>
                {coachAdvices?.length>0&&coachAdvicesTs&&!coachLoading&&(
                  <div style={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                    <span style={{fontSize:11,color:C.subtle,fontFamily:FONT_BODY}}>Aggiornato {fmtDateIt(coachAdvicesTs)}</span>
                    <button onClick={handleRichiediAnalisi} style={{border:'none',background:'transparent',padding:0,cursor:'pointer',fontSize:12,fontWeight:600,color:C.blue,fontFamily:FONT_BODY}}>Aggiorna</button>
                  </div>
                )}
                <button onClick={()=>onGoToCoach&&onGoToCoach(next.name)} className="gp-btn-press"
                  style={{display:'flex',alignItems:'center',justifyContent:'center',gap:8,width:'100%',padding:'14px 0',
                    border:'none',background:col,cursor:'pointer',fontFamily:FONT_BODY,fontSize:15,fontWeight:700,
                    color:'#fff',WebkitTapHighlightColor:'transparent',borderRadius:100,
                    transition:`transform 0.18s ${SPRING}, opacity 0.15s`}}>
                  <BrainElectricity width={16} height={16} color='#fff' strokeWidth={2}/>
                  Apri analisi
                </button>
              </div>
            </div>

          </div>
        ):null;

        const otherSchede=ranked.slice(1);

        return(
          <div>
            {nextWidget&&(
              <div style={{marginTop:20,animation:`gpSlideUp 0.5s 0.0s ${SPRING} both`}}>
                <div style={{fontSize:13,fontWeight:600,color:C.subtle,fontFamily:FONT_BODY,textTransform:'uppercase',letterSpacing:0.4,marginBottom:8}}>Prossima sessione</div>
                {nextWidget}
              </div>
            )}
            {otherSchede.length>0&&(
              <div style={{marginTop:18,animation:`gpSlideUp 0.52s 0.16s ${SPRING} both`}}>
                <div style={{fontSize:13,fontWeight:600,color:C.subtle,fontFamily:FONT_BODY,textTransform:'uppercase',letterSpacing:0.4,marginBottom:8}}>LE TUE SCHEDE</div>
                <div style={{...CARD_STYLE,overflow:'hidden',padding:0,background:'#fff'}}>
                  {otherSchede.map((sc,idx)=>{
                    const scCol=colorMap[sc.name]||C.blue;
                    let cached=null;
                    try{const r=localStorage.getItem(`gp_coach_advices_${sc.name}`);if(r)cached=JSON.parse(r);}catch{}
                    const scAdvices=cached?.advices||null;
                    const scTs=cached?.ts||null;
                    const latestSess=db.sessions.filter(s=>s.workoutName===sc.name).sort((a,b)=>b.date.localeCompare(a.date))[0];
                    const scStale=!!(latestSess&&scTs&&new Date(latestSess.date).getTime()>scTs);
                    return(<SchedaRow key={sc.name} scheda={sc} col={scCol} advices={scAdvices} ts={scTs} isStale={scStale} isLast={idx===otherSchede.length-1} onTap={()=>onGoToCoach&&onGoToCoach(sc.name)}/>);
                  })}
                </div>
              </div>
            )}
          </div>
        );
      })()}







      {/* Performance 4-state */}
      {Object.keys(perfMap).length>0&&widgets.performance&&(
        <>
        <div style={{fontSize:13,fontWeight:600,color:C.subtle,fontFamily:FONT_BODY,marginTop:16,marginBottom:8,animation:'fadeInDash 0.7s 0.44s ease both',textTransform:'uppercase'}}>Performance</div>
        <div style={{...St.card,animation:'fadeInDash 0.7s 0.46s ease both'}}>
          {['PR','Progressione','Regressione','Stallo']
            .map(state=>({state,items:Object.entries(perfMap).filter(([,v])=>v.state===state)}))
            .filter(({items})=>items.length>0)
            .map(({state,items},gi,arr)=>(
              <div key={state} style={{paddingBottom:gi<arr.length-1?12:0,marginBottom:gi<arr.length-1?12:0,borderBottom:gi<arr.length-1?`0.5px solid ${C.border}`:"none"}}>
                {items.map(([name,{color,pctChange}],i,iArr)=>(
                  <div key={name} onClick={()=>openExHistory(name)}
                    style={{display:"flex",alignItems:"center",gap:10,padding:"10px 0",cursor:"pointer",
                      borderBottom:i<iArr.length-1?`0.5px solid ${C.border}`:"none"}}>
                    <div style={{width:8,height:8,borderRadius:4,background:color,flexShrink:0}}/>
                    <div style={{flex:1,fontSize:14,fontWeight:600,color:C.text}}>{name}</div>
                    <span style={{fontSize:12,fontWeight:700,color}}>{state==='PR'?'PR':`${pctChange>=0?'+':''}${pctChange.toFixed(0)}%`}</span>
                  </div>
                ))}
              </div>
            ))}
        </div>
        </>
      )}


      {/* Navigazione rapida */}
      <div style={{fontSize:13,fontWeight:600,color:C.subtle,fontFamily:FONT_BODY,marginTop:20,marginBottom:8,textTransform:'uppercase'}}>Accesso rapido</div>
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:10}}>
        <button onClick={onGoToCronologia}
          style={{
            display:'flex',flexDirection:'column',alignItems:'flex-start',gap:4,
            padding:'14px 14px',borderRadius:10,border:'1px solid #EAEAEA',cursor:'pointer',
            background:'#ffffff',fontFamily:FONT_BODY,textAlign:'left',
            WebkitTapHighlightColor:'transparent',
          }}>
          <div style={{width:36,height:36,borderRadius:10,background:'rgba(0,122,255,0.12)',display:'flex',alignItems:'center',justifyContent:'center',marginBottom:6}}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={C.blue} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M4 6h16M4 10h16M4 14h10M4 18h7"/></svg>
          </div>
          <div style={{fontSize:13,fontWeight:700,color:C.text}}>Cronologia</div>
          <div style={{fontSize:11,color:C.subtle}}>{db.sessions.length} sessioni</div>
        </button>
        <button onClick={onGoToEsercizi}
          style={{
            display:'flex',flexDirection:'column',alignItems:'flex-start',gap:4,
            padding:'14px 14px',borderRadius:10,border:'1px solid #EAEAEA',cursor:'pointer',
            background:'#ffffff',fontFamily:FONT_BODY,textAlign:'left',
            WebkitTapHighlightColor:'transparent',
          }}>
          <div style={{width:36,height:36,borderRadius:10,background:'rgba(88,86,214,0.12)',display:'flex',alignItems:'center',justifyContent:'center',marginBottom:6}}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={C.purple} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M6.5 6.5h11M6.5 12h11M6.5 17.5h7M3 6.5h.5M3 12h.5M3 17.5h.5"/></svg>
          </div>
          <div style={{fontSize:13,fontWeight:700,color:C.text}}>Esercizi</div>
          <div style={{fontSize:11,color:C.subtle}}>Libreria</div>
        </button>
        <button onClick={onGoToSchede}
          style={{
            display:'flex',flexDirection:'column',alignItems:'flex-start',gap:4,
            padding:'14px 14px',borderRadius:10,border:'1px solid #EAEAEA',cursor:'pointer',
            background:'#ffffff',fontFamily:FONT_BODY,textAlign:'left',
            WebkitTapHighlightColor:'transparent',
          }}>
          <div style={{width:36,height:36,borderRadius:10,background:'rgba(255,149,0,0.12)',display:'flex',alignItems:'center',justifyContent:'center',marginBottom:6}}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={C.amber} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="7" width="18" height="14" rx="3"/><path d="M3 11h18M7 7V5a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v2"/></svg>
          </div>
          <div style={{fontSize:13,fontWeight:700,color:C.text}}>Schede</div>
          <div style={{fontSize:11,color:C.subtle}}>{(db.schede||[]).length} schede</div>
        </button>
      </div>

      </div>{/* end padding wrapper */}


      {/* Session day detail — full screen */}
      {selectedDayISO&&(()=>{
        const daySessions=[...db.sessions].filter(s=>s.date===selectedDayISO).sort((a,b)=>b.date.localeCompare(a.date));
        if(!daySessions.length)return null;
        const sess=daySessions[0];
        const col=colorMap[sess.workoutName]||C.blue;
        const dateLabel=new Date(selectedDayISO).toLocaleDateString("it-IT",{weekday:"long",day:"numeric",month:"long",year:"numeric"});
        const prevSessions=[...db.sessions].sort((a,b)=>b.date.localeCompare(a.date)).filter(s=>s.date<selectedDayISO);
        return(
          <div style={{position:"fixed",inset:0,zIndex:300,background:"#F5F2ED",display:"flex",flexDirection:"column",overflowY:"auto"}}>
            {/* Sticky header */}
            <div style={{position:"sticky",top:0,background:"rgba(245,242,237,0.94)",backdropFilter:"blur(20px)",WebkitBackdropFilter:"blur(20px)",zIndex:10,paddingTop:"env(safe-area-inset-top,12px)"}}>
              <div style={{padding:"10px 16px 12px",borderBottom:`0.5px solid ${C.border}`}}>
                <button onClick={()=>setSelectedDayISO(null)}
                  style={{background:"none",border:"none",cursor:"pointer",display:"flex",alignItems:"center",gap:5,color:col,padding:0,fontFamily:FONT_BODY,fontSize:15,fontWeight:500,marginBottom:10}}>
                  <NavArrowLeft width={18} height={18} color={col}/>
                  Indietro
                </button>
                <div style={{fontSize:20,fontWeight:700,color:C.text,letterSpacing:-0.5,lineHeight:1.2}}>{sess.workoutName||"Allenamento"}</div>
                <div style={{fontSize:12,color:C.subtle,marginTop:3,textTransform:"capitalize"}}>{dateLabel}</div>
              </div>
            </div>
            {/* Content */}
            <div style={{padding:"16px 16px max(32px,env(safe-area-inset-bottom,16px)) 16px"}}>
              <SessionStats exercises={sess.exercises} allSessions={db.sessions} date={sess.date}/>
              {sess.exercises.map((ex,i)=>{
                const prev=prevSessions.find(s=>s.exercises.some(e=>e.name.toLowerCase().trim()===ex.name.toLowerCase().trim()))?.exercises.find(e=>e.name.toLowerCase().trim()===ex.name.toLowerCase().trim());
                return <ExercisePreview key={i} ex={ex} prevEx={prev} allSessions={db.sessions} showInsights={true}/>;
              })}
            </div>
          </div>
        );
      })()}

      {/* Volume detail sheet */}
      {showVolumeDetail&&(
        <div style={{position:"fixed",inset:0,zIndex:200,display:"flex",flexDirection:"column"}}>
          <div onClick={()=>setShowVolumeDetail(false)} style={{flex:1,background:"rgba(0,0,0,0.28)"}}/>
          <div style={{background:C.card,borderRadius:"24px 24px 0 0",padding:"20px 20px max(20px,env(safe-area-inset-bottom,0px)) 20px",maxHeight:"75vh",overflowY:"auto"}}>
            <div style={{width:36,height:4,borderRadius:2,background:C.border,margin:"0 auto 16px"}}/>
            <div style={{fontWeight:700,fontSize:20,color:C.text,letterSpacing:-0.4,marginBottom:16}}>Volume settimanale</div>
            {wv.length>0&&(
              <div style={{marginBottom:20}}>
                <ResponsiveContainer width="100%" height={110}>
                  <BarChart data={wv} margin={{top:0,right:0,left:-30,bottom:0}}>
                    <XAxis dataKey="week" tick={{fontSize:10,fill:C.subtle}} axisLine={false} tickLine={false}/>
                    <YAxis hide/>
                    <Bar dataKey="volume" fill={C.blue} radius={[4,4,0,0]}/>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
            {weekSessions.length===0&&(
              <div style={{fontSize:13,color:C.subtle,paddingTop:8}}>Nessuna sessione questa settimana.</div>
            )}
            {weekSessions.map((s,i)=>{
              const vol=Math.round(s.exercises.reduce((acc,e)=>acc+calcVolume(e.sets||[]),0));
              const dLabel=new Date(s.date).toLocaleDateString("it-IT",{weekday:"short",day:"numeric",month:"short"});
              const col=colorMap[s.workoutName]||C.blue;
              return(
                <div key={s.id||i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"12px 0",borderBottom:i<weekSessions.length-1?`0.5px solid ${C.border}`:"none"}}>
                  <div style={{display:"flex",alignItems:"center",gap:10}}>
                    <div style={{width:8,height:8,borderRadius:"50%",background:col,flexShrink:0}}/>
                    <div>
                      <div style={{fontWeight:600,fontSize:14,color:C.text}}>{s.workoutName||"Allenamento"}</div>
                      <div style={{fontSize:11,color:C.subtle,marginTop:2,textTransform:"capitalize"}}>{dLabel}</div>
                    </div>
                  </div>
                  <span style={{fontFamily:FONT_MONO,fontSize:14,fontWeight:700,color:C.text}}>
                    {vol.toLocaleString()}{" "}<span style={{fontSize:11,fontWeight:400,color:C.subtle}}>vol</span>
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}


      {/* ── Area Utente ─────────────────────────────────────────────────────── */}
      {showUserArea&&<UserArea user={user} db={db} setDB={setDB} onClose={()=>setShowUserArea(false)} onReplayOnboarding={onReplayOnboarding}/>}
    </div>
  );
}

// ─── MINI CALENDAR ────────────────────────────────────────────────────────────
